import MarketingLayout from "@/components/MarketingLayout";
import { Shield } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <MarketingLayout>
      <section className="container py-16">
        <div className="max-w-3xl mx-auto space-y-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="w-6 h-6" /> Privacy Policy
            </h1>
            <p className="text-muted-foreground mt-1">
              Last updated: 8 March 2026
            </p>
          </div>

          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-sm leading-relaxed text-foreground">
            <section className="space-y-2">
              <h2 className="text-lg font-semibold">1. Controller</h2>
              <p className="text-muted-foreground">
                Double O ("we", "us", "our") operates the website{" "}
                <strong>oovault.nl</strong> and the Double O platform. We are the
                controller of the personal data processed through our services.
                For questions about this policy, contact us at{" "}
                <a href="mailto:privacy@oovault.nl" className="text-primary hover:underline">
                  privacy@oovault.nl
                </a>.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">2. Data We Collect</h2>
              <p className="text-muted-foreground">We collect and process the following categories of personal data:</p>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li>
                  <strong>Account data</strong> — name, email address, and profile
                  information provided through Auth0 authentication.
                </li>
                <li>
                  <strong>Billing data</strong> — payment information processed
                  securely by Stripe. We do not store full card numbers.
                </li>
                <li>
                  <strong>Usage data</strong> — API call counts, feature usage,
                  and session information necessary to operate the service.
                </li>
                <li>
                  <strong>Analytics data</strong> — anonymised page-view and
                  interaction data collected via Umami (a privacy-focused, cookieless
                  analytics tool).
                </li>
                <li>
                  <strong>Marketing attribution data</strong> — referrer URLs, UTM
                  parameters, and landing-page paths stored in a first-party cookie
                  to understand how you found us.
                </li>
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">3. Legal Basis for Processing (GDPR Art. 6)</h2>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li>
                  <strong>Contract performance</strong> (Art. 6(1)(b)) — processing
                  necessary to provide you with the Double O service.
                </li>
                <li>
                  <strong>Legitimate interest</strong> (Art. 6(1)(f)) — analytics,
                  fraud prevention, and service improvement.
                </li>
                <li>
                  <strong>Consent</strong> (Art. 6(1)(a)) — for non-essential
                  cookies such as marketing attribution. You may withdraw consent
                  at any time.
                </li>
                <li>
                  <strong>Legal obligation</strong> (Art. 6(1)(c)) — where required
                  for tax, accounting, or regulatory compliance.
                </li>
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">4. Cookies</h2>
              <p className="text-muted-foreground">We use the following cookies:</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-muted-foreground border border-border rounded">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left p-2 font-medium">Cookie</th>
                      <th className="text-left p-2 font-medium">Purpose</th>
                      <th className="text-left p-2 font-medium">Type</th>
                      <th className="text-left p-2 font-medium">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="p-2 font-mono text-xs">app_session_id</td>
                      <td className="p-2">Authenticates your session</td>
                      <td className="p-2">Strictly necessary</td>
                      <td className="p-2">Session</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="p-2 font-mono text-xs">double_o_attribution</td>
                      <td className="p-2">Tracks how you discovered Double O</td>
                      <td className="p-2">Marketing</td>
                      <td className="p-2">90 days</td>
                    </tr>
                    <tr>
                      <td className="p-2 font-mono text-xs">cookie_consent</td>
                      <td className="p-2">Remembers your cookie preferences</td>
                      <td className="p-2">Strictly necessary</td>
                      <td className="p-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground">
                Umami analytics does not use cookies and does not track personal
                data.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">5. Third-Party Processors</h2>
              <p className="text-muted-foreground">We share data with the following processors, each bound by data-processing agreements:</p>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li><strong>Auth0 (Okta)</strong> — authentication and identity management.</li>
                <li><strong>Stripe</strong> — payment processing and billing.</li>
                <li><strong>Umami</strong> — privacy-focused website analytics (self-hosted / EU-based).</li>
                <li><strong>Sentry</strong> — error monitoring and performance tracking.</li>
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">6. International Transfers</h2>
              <p className="text-muted-foreground">
                Some processors may transfer data outside the EEA. Where this
                occurs, we rely on EU-approved safeguards such as Standard
                Contractual Clauses (SCCs) or adequacy decisions to protect your data.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">7. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain personal data only as long as necessary for the purposes
                described above, or as required by law. Account data is deleted
                within 30 days of account closure. Billing records are retained for
                the statutory period required for tax purposes.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">8. Your Rights</h2>
              <p className="text-muted-foreground">Under the GDPR and applicable laws, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li>Access the personal data we hold about you.</li>
                <li>Rectify inaccurate or incomplete data.</li>
                <li>Erase your data ("right to be forgotten").</li>
                <li>Restrict or object to processing.</li>
                <li>Data portability — receive your data in a structured format.</li>
                <li>Withdraw consent at any time where processing is based on consent.</li>
                <li>Lodge a complaint with your local supervisory authority.</li>
              </ul>
              <p className="text-muted-foreground">
                To exercise any of these rights, email us at{" "}
                <a href="mailto:privacy@oovault.nl" className="text-primary hover:underline">
                  privacy@oovault.nl
                </a>.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">9. California Residents (CCPA)</h2>
              <p className="text-muted-foreground">
                If you are a California resident, you have the right to know what
                personal information we collect, request its deletion, and opt out
                of any sale of personal information. We do <strong>not</strong> sell
                personal information. To submit a request, contact us at{" "}
                <a href="mailto:privacy@oovault.nl" className="text-primary hover:underline">
                  privacy@oovault.nl
                </a>.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">10. Security</h2>
              <p className="text-muted-foreground">
                We implement appropriate technical and organisational measures to
                protect your data, including encryption in transit (TLS),
                access controls, and regular security reviews.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">11. Children</h2>
              <p className="text-muted-foreground">
                Double O is not directed at individuals under 16. We do not
                knowingly collect data from children. If you believe we have, please
                contact us so we can promptly delete it.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">12. Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this policy from time to time. Material changes will
                be communicated via email or a notice on the website. Continued use
                of the service after changes constitutes acceptance.
              </p>
            </section>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
